import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import { useNotify } from 'react-admin';
import PublicIcon from '@mui/icons-material/Public';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import HistoryIcon from '@mui/icons-material/History';
import PushService, { type DevicesWhere, type Stay, type WhereCountry, type WherePerson, type WhereWindow } from '../../services/push/pushService.ts';
import { errorText, fmtAgo } from '../attractions/attractionsShared.tsx';

const pushService = new PushService();

/** Cyprus is home: it is shown first whatever its count. */
const HOME = 'CY';

const WINDOW_LABELS: Record<WhereWindow, string> = {
    today: 'Today',
    '7d': '7 days',
    all: 'All'
};

/** What "nobody" means for each window, so the empty state says it in words. */
const WINDOW_TEXT: Record<WhereWindow, string> = {
    today: 'in the last 24 hours',
    '7d': 'in the last 7 days',
    all: 'at all'
};

/** "CY" -> the flag emoji, built from the regional indicator letters. Anything that is not two letters gets none. */
const flagOf = (code: string | null | undefined): string => {
    if (!code || !/^[A-Za-z]{2}$/.test(code)) return '';
    return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
};

/** The exact instant, for the hover behind a relative time. */
const fmtExact = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) });
};

/** A relative time with the exact one on hover; a dash when there is none. */
const Ago = ({ iso }: { iso: string | null | undefined }) => {
    const text = fmtAgo(iso);
    if (!text) return <>—</>;
    return (
        <Tooltip title={fmtExact(iso)} placement="top-start">
            <span>{text}</span>
        </Tooltip>
    );
};

/**
 * Country name for a 2-letter code: the server's list first (it names what it
 * counts), then the browser's own names, then the code itself.
 */
const useCountryName = (countries: WhereCountry[]) =>
    useMemo(() => {
        const fromServer = new Map(countries.map((c) => [c.code.toUpperCase(), c.name]));
        let display: Intl.DisplayNames | null = null;
        try {
            display = new Intl.DisplayNames(['en'], { type: 'region' });
        } catch {
            display = null;
        }
        return (code: string | null | undefined): string => {
            if (!code) return '';
            const upper = code.toUpperCase();
            const known = fromServer.get(upper);
            if (known) return known;
            try {
                return display?.of(upper) ?? upper;
            } catch {
                return upper;
            }
        };
    }, [countries]);

/** One country: flag, name, how many phones and how many of them are signed-in customers. */
const CountryTile = ({ country, home }: { country: WhereCountry; home: boolean }) => (
    <Box
        sx={{
            border: 1,
            borderColor: home ? 'primary.main' : 'divider',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            minWidth: 150
        }}
    >
        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {flagOf(country.code)} {country.name || country.code}
        </Typography>
        <Typography variant="h6" sx={{ lineHeight: 1.1, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
            {country.devices.toLocaleString('en-GB')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {country.devices === 1 ? 'device' : 'devices'} · {country.customers.toLocaleString('en-GB')}{' '}
            {country.customers === 1 ? 'customer' : 'customers'}
        </Typography>
    </Box>
);

type SortField = 'lastSeenAt' | 'country';

/**
 * Notifications › Where are my customers.
 *
 * <p>Counts per country and the people behind them, from the time zone each
 * phone reports. The point is to know who is on the island right now and who
 * is at home abroad, so a push lands where someone can act on it. The server
 * does the counting; this page only chooses the window and sorts.
 */
const WhereCustomersPage = () => {
    const notify = useNotify();
    const [period, setPeriod] = useState<WhereWindow>('today');
    const [data, setData] = useState<DevicesWhere | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState<SortField>('lastSeenAt');
    const [sortAsc, setSortAsc] = useState(false);
    const [travel, setTravel] = useState<{ person: WherePerson; stays: Stay[] | null } | null>(null);

    // The movement history: every country this phone (or every phone of this
    // customer) has been in, newest first. Country level only, like the page.
    const showTravel = useCallback(async (p: WherePerson) => {
        setTravel({ person: p, stays: null });
        try {
            const stays = await pushService.stays(p.customerId ? { customerId: p.customerId } : { deviceId: p.deviceId });
            setTravel({ person: p, stays });
        } catch (e) {
            setTravel(null);
            notify(errorText(e, 'Could not load the travel history'), { type: 'warning' });
        }
    }, [notify]);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(null);
        try {
            setData(await pushService.where(period));
        } catch (e) {
            // An empty page and a broken one look the same; say which it is.
            setData(null);
            setFailed(errorText(e, 'Could not load where the customers are. Reload the page to try again.'));
            notify('Could not load where the customers are', { type: 'warning' });
        } finally {
            setLoading(false);
        }
    }, [period, notify]);

    useEffect(() => {
        void load();
    }, [load]);

    // The landing test without a flight: pretend the phone reported Cyprus
    // time (or Israel time). The server treats it exactly like a real report,
    // so the visit opens or closes and the welcome and meal pushes follow.
    const simulate = useCallback(async (p: WherePerson, zone: 'Asia/Nicosia' | 'Asia/Jerusalem') => {
        if (!p.deviceId) return;
        const what = zone === 'Asia/Nicosia' ? 'landed in Cyprus' : 'left Cyprus';
        if (!window.confirm(`Pretend ${p.name?.trim() || 'this phone'} just ${what}? Real pushes will be queued for it.`)) return;
        try {
            await pushService.simulateZone(p.deviceId, zone);
            notify(`${p.name?.trim() || 'The phone'} now counts as having ${what}`, { type: 'info' });
            await load();
        } catch (e) {
            notify(errorText(e, 'Could not simulate that'), { type: 'warning' });
        }
    }, [notify, load]);

    const countries = useMemo(() => data?.countries ?? [], [data]);
    const nameOf = useCountryName(countries);

    // Most phones first, but home first of all.
    const tiles = useMemo(() => {
        const sorted = [...countries].sort((a, b) => b.devices - a.devices);
        const home = sorted.findIndex((c) => c.code.toUpperCase() === HOME);
        if (home > 0) sorted.unshift(...sorted.splice(home, 1));
        return sorted;
    }, [countries]);
    const totalDevices = countries.reduce((sum, c) => sum + c.devices, 0);
    const totalCustomers = countries.reduce((sum, c) => sum + c.customers, 0);

    const people = useMemo(() => {
        const q = search.trim().toLowerCase();
        const rows = (data?.people ?? []).filter((p) => {
            if (!q) return true;
            return [p.name, p.phone, p.country, nameOf(p.country)]
                .some((v) => (v ?? '').toLowerCase().includes(q));
        });
        const dir = sortAsc ? 1 : -1;
        return rows.sort((a, b) => {
            if (sortField === 'country') {
                const byName = nameOf(a.country).localeCompare(nameOf(b.country));
                if (byName !== 0) return byName * dir;
            } else {
                const at = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0;
                const bt = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0;
                if (at !== bt) return (at - bt) * dir;
            }
            // Ties: most recently seen first, whatever the column.
            return (b.lastSeenAt ?? '').localeCompare(a.lastSeenAt ?? '');
        });
    }, [data, search, sortField, sortAsc, nameOf]);

    const sortBy = (field: SortField) => {
        if (field === sortField) {
            setSortAsc(!sortAsc);
        } else {
            setSortField(field);
            // Newest first for time, A to Z for a country.
            setSortAsc(field === 'country');
        }
    };

    const headCell = (field: SortField, label: string) => (
        <TableCell sortDirection={sortField === field ? (sortAsc ? 'asc' : 'desc') : false}>
            <TableSortLabel
                active={sortField === field}
                direction={sortField === field && sortAsc ? 'asc' : 'desc'}
                onClick={() => sortBy(field)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const totalPeople = data?.people.length ?? 0;
    const countLine = search.trim() && people.length !== totalPeople
        ? `${people.length.toLocaleString('en-GB')} of ${totalPeople.toLocaleString('en-GB')} ${totalPeople === 1 ? 'person' : 'people'} match`
        : `${totalPeople.toLocaleString('en-GB')} ${totalPeople === 1 ? 'person' : 'people'}`;

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <PublicIcon color="primary" />
                <Typography variant="h5">Where are my customers</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 680 }}>
                Country comes from the phone's time zone, reported when the app opens or on the twice-daily silent
                check (app 1.11+). Nothing finer than country is stored.
            </Typography>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={period}
                            onChange={(_, value) => { if (value) setPeriod(value); }}
                        >
                            {(Object.keys(WINDOW_LABELS) as WhereWindow[]).map((key) => (
                                <ToggleButton key={key} value={key}>
                                    {WINDOW_LABELS[key]}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <Typography variant="caption" color="text.secondary">
                            {period === 'today' ? 'Seen in the last 24 hours' : period === '7d' ? 'Seen in the last 7 days' : 'Every phone ever seen'}
                            {data?.generatedAt ? ` · as of ${fmtExact(data.generatedAt)}` : ''}
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>

            {failed && <Alert severity="error" sx={{ mb: 2 }}>{failed}</Alert>}

            {loading ? (
                <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : data && (
                <>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6">By country</Typography>
                            {tiles.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    No phones seen {WINDOW_TEXT[period]}.
                                </Typography>
                            ) : (
                                <>
                                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                        {tiles.map((c) => (
                                            <CountryTile key={c.code} country={c} home={c.code.toUpperCase() === HOME} />
                                        ))}
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                        Total: {totalDevices.toLocaleString('en-GB')} {totalDevices === 1 ? 'device' : 'devices'} ·{' '}
                                        {totalCustomers.toLocaleString('en-GB')} {totalCustomers === 1 ? 'customer' : 'customers'} ·{' '}
                                        {tiles.length} {tiles.length === 1 ? 'country' : 'countries'}
                                    </Typography>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={2} sx={{ mb: 1 }}>
                                <Box>
                                    <Typography variant="h6">People</Typography>
                                    <Typography variant="body2" color="text.secondary">{countLine}</Typography>
                                </Box>
                                <TextField
                                    id="where-search"
                                    label="Search"
                                    placeholder="Name, phone or country"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    size="small"
                                    sx={{ width: { xs: '100%', sm: 280 } }}
                                />
                            </Stack>

                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Phone</TableCell>
                                        {headCell('country', 'Country')}
                                        <TableCell>Time zone</TableCell>
                                        {headCell('lastSeenAt', 'Last seen')}
                                        <TableCell>Last order</TableCell>
                                        <TableCell>Local / visitor</TableCell>
                                        <TableCell>Here since</TableCell>
                                        <TableCell align="right">History · test</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {people.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={9}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {search.trim()
                                                        ? `Nothing matches “${search.trim()}”.`
                                                        : `Nobody seen ${WINDOW_TEXT[period]}.`}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {people.map((p: WherePerson) => (
                                        <TableRow key={p.deviceId} hover>
                                            <TableCell>
                                                {p.name?.trim() || (p.customerId ? `Customer #${p.customerId}` : '—')}
                                                {p.source === 'CUSTOMER_PROFILE' && (
                                                    <Tooltip title="Reported by the app at last open; updates when the phone runs 1.11" placement="top-start">
                                                        <Chip size="small" label="older app" variant="outlined" sx={{ ml: 1 }} />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.phone ?? '—'}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {p.country ? `${flagOf(p.country)} ${nameOf(p.country)}`.trim() : '—'}
                                            </TableCell>
                                            <TableCell>{p.timeZone ?? '—'}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}><Ago iso={p.lastSeenAt} /></TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {p.lastOrderAt || p.lastOrderCity
                                                    ? [fmtDate(p.lastOrderAt), p.lastOrderCity].filter(Boolean).join(' · ')
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                {p.isLocal == null ? '—' : (
                                                    <Chip
                                                        size="small"
                                                        label={p.isLocal ? 'Local' : 'Visitor'}
                                                        color={p.isLocal ? 'default' : 'info'}
                                                        variant="outlined"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}><Ago iso={p.tripArrivedAt} /></TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                {p.deviceId ? (
                                                    <>
                                                        <Tooltip title="Simulate landing in Cyprus (queues the welcome and the meal pushes for this phone)">
                                                            <Tooltip title="Where they have been">
                                                                <IconButton size="small" onClick={() => void showTravel(p)} aria-label="travel history">
                                                                    <HistoryIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <IconButton size="small" onClick={() => void simulate(p, 'Asia/Nicosia')} aria-label="simulate landing">
                                                                <FlightLandIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Simulate leaving Cyprus (ends the visit, drops its pending pushes)">
                                                            <IconButton size="small" onClick={() => void simulate(p, 'Asia/Jerusalem')} aria-label="simulate leaving">
                                                                <FlightTakeoffIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                ) : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}
            <Dialog open={!!travel} onClose={() => setTravel(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {travel?.person.name?.trim() || 'This phone'}: where they have been
                </DialogTitle>
                <DialogContent>
                    {!travel?.stays ? (
                        <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>
                    ) : travel.stays.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            No history yet. It starts with the first time zone the phone reports on app 1.11.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Country</TableCell>
                                    <TableCell>From</TableCell>
                                    <TableCell>To</TableCell>
                                    <TableCell align="right">Nights</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {travel.stays.map((s) => (
                                    <TableRow key={s.id}>
                                        <TableCell>
                                            {s.country ? `${flagOf(s.country)} ${s.countryName ?? s.country}` : s.timeZone}
                                            {s.source === 'ADMIN_TEST' && (
                                                <Typography variant="caption" color="text.secondary" display="block">simulated</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{fmtExact(s.arrivedAt)}</TableCell>
                                        <TableCell>{s.leftAt ? fmtExact(s.leftAt) : <Chip size="small" color="primary" variant="outlined" label="now" />}</TableCell>
                                        <TableCell align="right">{s.nights}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default WhereCustomersPage;
